import { useCallback, useEffect, useMemo, useState } from "react";

import GMNetwork from "../lib";
import {
  AAProvider,
  Account,
  ChainInfo,
  Config,
  Options,
  Transaction,
  CreateConfig,
  ConnectConfig,
  AccountAbstraction,
} from "../types";

export const useGMNetwork = (
  config?: Config,
  options?: Options,
  clean?: boolean
) =>
  useMemo(() => {
    if (clean) {
      GMNetwork.clean();
    }

    const current = GMNetwork.getGMNetwork();
    if (!!current) return current;

    if (!config?.appId || !config?.projectId || !config?.clientKey) return null;

    return GMNetwork.init(config, options);
  }, [config, clean]);

export const useCreate = () => {
  const core = useGMNetwork();

  return useCallback(async (options?: CreateConfig) => {
    if (core!.connected) {
      await core!.disconnect();
    }

    return await core!.create(options);
  }, []);
};

export const useConnect = () => {
  const core = useGMNetwork();

  return useCallback(async (options?: ConnectConfig) => {
    if (!core!.connected) {
      return await core!.connect(options);
    }
    return true;
  }, []);
};

export const useAvailable = (flag?: number) => {
  const core = useGMNetwork();

  const [available, setAvailable] = useState<boolean | null>(null);

  useEffect(() => {
    const _getConfig = async () => {
      setAvailable((await core?.isAvailable()) || null);
    };
    _getConfig().finally(() => {});
  }, [core, flag]);

  return available;
};

export const useConnected = (flag?: number) => {
  const core = useGMNetwork();

  const [data, setData] = useState<boolean | null>(null);

  useEffect(() => {
    setData(core?.connected || null);
  }, [core, flag]);

  return data;
};

export const useDisconnect = () => {
  const core = useGMNetwork();

  return useCallback(async () => {
    return await core!.disconnect();
  }, []);
};

export const useChainId = (flag?: number) => {
  const core = useGMNetwork();

  const [data, setData] = useState<number | null>(null);

  useEffect(() => {
    setData(core?.chainId || null);
  }, [core, flag]);

  return data;
};

export const useChangeChainId = () => {
  const core = useGMNetwork();

  return useCallback(async (chainId: number) => {
    return await core!.setChainId(chainId);
  }, []);
};

export const useSupportedChainIds = (flag?: number) => {
  const core = useGMNetwork();

  const [data, setData] = useState<number[] | null>(null);

  useEffect(() => {
    setData(core?.supportedChainIds || null);
  }, [core, flag]);

  return data;
};

export const useSupportedChains = (flag?: number) => {
  const core = useGMNetwork();

  const [data, setData] = useState<ChainInfo[] | null>(null);

  useEffect(() => {
    setData(core?.supportedChains || null);
  }, [core, flag]);

  return data;
};

export const useProvider = (flag?: number) => {
  const core = useGMNetwork();

  const [data, setData] = useState<AAProvider | null | undefined>(() =>
    core?.connected ? core.provider : null
  );

  useEffect(() => {
    setData(core?.connected ? core.provider : null);
  }, [core, flag]);

  return data;
};

export const useAA = (flag?: number) => {
  const core = useGMNetwork();

  const [data, setData] = useState<AccountAbstraction | null>(null);

  useEffect(() => {
    setData(core?.wallet || null);
  }, [core, flag]);

  return data;
};

export const useAccount = (flag?: number) => {
  const core = useGMNetwork();

  const [account, setAccount] = useState<Account | null>(null);

  useEffect(() => {
    const _getConfig = async () => {
      setAccount((await core?.wallet?.getAccount()) || null);
    };
    _getConfig().finally(() => {});
  }, [core, flag]);

  return account;
};

export const useAddress = (flag?: number) => {
  const core = useGMNetwork();

  const [data, setData] = useState<string | null>(null);

  useEffect(() => {
    const _getConfig = async () => {
      setData((await core?.wallet?.getAddress()) || null);
    };
    _getConfig().finally(() => {});
  }, [core, flag]);

  return data;
};

export const useOwner = (flag?: number) => {
  const core = useGMNetwork();

  const [data, setData] = useState<string | null>(null);

  useEffect(() => {
    const _getConfig = async () => {
      setData((await core?.wallet?.getOwner()) || null);
    };
    _getConfig().finally(() => {});
  }, [core, flag]);

  return data;
};

export const useSendTransaction = () => {
  const core = useGMNetwork();

  return useCallback(
    async (tx: Transaction) => {
      if (!!core?.wallet) {
        return core?.wallet.sendTransaction(tx);
      }
      return null;
    },
    [core]
  );
};
